import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useUser } from '@/hooks/useUser'
import { api } from '@/lib/api/client'
import type { ArtifactData, ArtifactMetadata } from '@shared/types/artifact'
import type { IdentityData, IdentityMetadata } from '@shared/types/identity'
import type { ThemeMetadata } from '@shared/types/theme'

interface OpenArtifactWindow {
  id: string
  artifactId: string
  windowId: number
}

interface StorageContextType {
  // Artifact fields
  artifacts: ArtifactMetadata[]
  isLoading: boolean
  openWindows: OpenArtifactWindow[]
  fetchArtifacts: () => Promise<void>
  openArtifact: (artifactId: string) => void
  closeArtifact: (windowId: number) => void
  getArtifact: (artifactId: string) => Promise<ArtifactData | null>
  deleteArtifact: (artifactId: string) => Promise<boolean>

  // Theme fields
  themes: ThemeMetadata[]
  isLoadingThemes: boolean
  fetchThemes: () => Promise<void>

  // Identity fields
  identities: IdentityMetadata[]
  isLoadingIdentities: boolean
  fetchIdentities: () => Promise<void>
  getIdentity: (identityId: string) => Promise<IdentityData | null>
  deleteIdentity: (identityId: string) => Promise<boolean>
}

const StorageContext = createContext<StorageContextType | undefined>(undefined)

let nextWindowId = 1

export function StorageProvider({ children }: { children: ReactNode }) {
  const { user } = useUser()
  const [artifacts, setArtifacts] = useState<ArtifactMetadata[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [openWindows, setOpenWindows] = useState<OpenArtifactWindow[]>([])

  const [themes, setThemes] = useState<ThemeMetadata[]>([])
  const [isLoadingThemes, setIsLoadingThemes] = useState(false)

  const [identities, setIdentities] = useState<IdentityMetadata[]>([])
  const [isLoadingIdentities, setIsLoadingIdentities] = useState(false)

  const fetchArtifacts = useCallback(async () => {
    if (!user) return
    setIsLoading(true)
    try {
      const { data, error } = await api.api.artifacts.list.get()
      if (error) {
        console.error('Failed to fetch artifacts:', error)
      } else {
        setArtifacts(data?.artifacts || [])
      }
    } catch (error) {
      console.error('Error fetching artifacts:', error)
    } finally {
      setIsLoading(false)
    }
  }, [user])

  const fetchThemes = useCallback(async () => {
    if (!user) return
    setIsLoadingThemes(true)
    try {
      const { data, error } = await api.api.themes.list.get()
      if (error) {
        console.error('Failed to fetch themes:', error)
      } else {
        setThemes(data?.themes || [])
      }
    } catch (error) {
      console.error('Error fetching themes:', error)
    } finally {
      setIsLoadingThemes(false)
    }
  }, [user])

  const getArtifact = useCallback(
    async (artifactId: string): Promise<ArtifactData | null> => {
      if (!user) return null
      try {
        const { data, error } = await api.api.artifacts({ id: artifactId }).get()
        if (error) {
          console.error('Failed to fetch artifact:', error)
          return null
        }
        return data?.artifact || null
      } catch (error) {
        console.error('Error fetching artifact:', error)
        return null
      }
    },
    [user]
  )

  const openArtifact = useCallback(
    (artifactId: string) => {
      if (!user) return
      // Check if artifact is already open
      const existingWindow = openWindows.find(
        (w) => w.artifactId === artifactId
      )
      if (existingWindow) {
        // Focus existing window (handled by WindowManager)
        return
      }

      // Create new window
      const newWindow: OpenArtifactWindow = {
        id: `artifact-window-${nextWindowId}`,
        artifactId,
        windowId: nextWindowId++,
      }
      setOpenWindows((prev) => [...prev, newWindow])
    },
    [user, openWindows]
  )

  const closeArtifact = useCallback((windowId: number) => {
    setOpenWindows((prev) => prev.filter((w) => w.windowId !== windowId))
  }, [])

  const deleteArtifact = useCallback(
    async (artifactId: string): Promise<boolean> => {
      if (!user) return false
      try {
        const { error } = await api.api.artifacts({ id: artifactId }).delete()

        if (!error) {
          // Refresh the artifacts list
          await fetchArtifacts()
          // Close any windows for this artifact
          setOpenWindows((prev) =>
            prev.filter((w) => w.artifactId !== artifactId)
          )
          return true
        } else {
          console.error('Failed to delete artifact:', error)
          return false
        }
      } catch (error) {
        console.error('Error deleting artifact:', error)
        return false
      }
    },
    [user, fetchArtifacts]
  )

  const fetchIdentities = useCallback(async () => {
    if (!user) return
    setIsLoadingIdentities(true)
    try {
      const { data, error } = await api.api.identities.list.get()
      if (error) {
        console.error('Failed to fetch identities:', error)
      } else {
        setIdentities(data?.identities || [])
      }
    } catch (error) {
      console.error('Error fetching identities:', error)
    } finally {
      setIsLoadingIdentities(false)
    }
  }, [user])

  const getIdentity = useCallback(
    async (identityId: string): Promise<IdentityData | null> => {
      if (!user) return null
      try {
        const { data, error } = await api.api.identities({ id: identityId }).get()
        if (error) {
          console.error('Failed to fetch identity:', error)
          return null
        }
        return data?.identity || null
      } catch (error) {
        console.error('Error fetching identity:', error)
        return null
      }
    },
    [user]
  )

  const deleteIdentity = useCallback(
    async (identityId: string): Promise<boolean> => {
      if (!user) return false
      try {
        const { error } = await api.api.identities({ id: identityId }).delete()

        if (!error) {
          // Refresh the identities list
          await fetchIdentities()
          return true
        } else {
          console.error('Failed to delete identity:', error)
          return false
        }
      } catch (error) {
        console.error('Error deleting identity:', error)
        return false
      }
    },
    [user, fetchIdentities]
  )

  // Fetch artifacts, themes, and identities on mount
  useEffect(() => {
    if (!user) return
    fetchArtifacts()
    fetchThemes()
    fetchIdentities()
  }, [user, fetchArtifacts, fetchThemes, fetchIdentities])

  return (
    <StorageContext.Provider
      value={{
        artifacts,
        isLoading,
        openWindows,
        fetchArtifacts,
        openArtifact,
        closeArtifact,
        getArtifact,
        deleteArtifact,
        themes,
        isLoadingThemes,
        fetchThemes,
        identities,
        isLoadingIdentities,
        fetchIdentities,
        getIdentity,
        deleteIdentity,
      }}
    >
      {children}
    </StorageContext.Provider>
  )
}

export function useStorage() {
  const context = useContext(StorageContext)
  if (context === undefined) {
    throw new Error('useStorage must be used within a StorageProvider')
  }
  return context
}

// Legacy export for backward compatibility during migration
export const useArtifacts = useStorage
